import React from 'react';
import { Alert, AlertTitle, Box, Snackbar, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import {
  GeofenceAlert,
  geofenceAlertDismissed,
} from '../../store/slices/safetySlice';

/**
 * Geofence boundary alerts (#452).
 *
 * A drone that reaches its fence turns back on its own — this is how the
 * operator finds out it happened. The alerts do not time out: a drone that broke
 * its boundary is not something to notice only if you happened to be looking at
 * the right moment, so they stay until dismissed.
 */

/** Describes what the drone did, in the operator's terms. */
const describeAlert = (alert: GeofenceAlert): string => {
  const distance =
    alert.kind === 'approaching_boundary'
      ? `${Math.round(alert.distanceM)} m from the boundary`
      : `${Math.round(alert.distanceM)} m outside the boundary`;
  // After a lost fix the board measured from where the fix was lost; the
  // drone may have moved since (#842, B28).
  const where = alert.positionIsLastFixed ? `${distance} at its last GPS fix` : distance;
  // A drone that could not start its return is relying on the flight
  // controller's fence alone. That is the part an operator most needs to read.
  const what =
    {
      return_to_base: 'returning to base',
      return_to_base_failed:
        'the onboard return did not start — the flight controller’s fence is the only protection',
    }[alert.action] ?? `action taken: ${alert.action}`;
  return `${where} — ${what}.`;
};

const alertTitle = (alert: GeofenceAlert, droneName: string): string =>
  alert.kind === 'approaching_boundary'
    ? `${droneName} turned back at its geofence`
    : `${droneName} left its geofence`;

export const GeofenceAlerts: React.FC = () => {
  const dispatch = useDispatch();
  const alerts = useSelector((state: RootState) => state.safety.geofenceAlerts);
  const drones = useSelector((state: RootState) => state.drones.drones);

  if (alerts.length === 0) return null;

  // Fall back to the ID: an alert about a drone the UI has not loaded yet is
  // still an alert worth showing.
  const nameFor = (droneId: string): string => drones[droneId]?.name ?? droneId;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      sx={{ maxWidth: { xs: '100%', sm: 420 } }}
    >
      <Box
        role="region"
        aria-label="Geofence alerts"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            // Crossing the line is a breach; turning back before it is the
            // system working as intended. They should not look the same.
            severity={alert.kind === 'outside_boundary' ? 'error' : 'warning'}
            role="alert"
            onClose={() => dispatch(geofenceAlertDismissed(alert.id))}
            variant="filled"
          >
            <AlertTitle>{alertTitle(alert, nameFor(alert.droneId))}</AlertTitle>
            <Typography variant="body2">{describeAlert(alert)}</Typography>
          </Alert>
        ))}
      </Box>
    </Snackbar>
  );
};
